import React from 'react';
import {
  MenuItem,
  Select as MuiSelect,
  FormControl,
  InputLabel,
  FormHelperText,
  styled,
  useTheme,
} from '@mui/material';

const StyledInputLabel = styled(({ customStyles: _customStyles, ...otherProps }) => (
  <InputLabel {...otherProps} />
))(({ theme, customStyles }) => ({
  color: customStyles?.color || theme.palette?.select?.label || theme.palette.text.secondary,
  '&.Mui-focused': {
    color: customStyles?.focusedColor || theme.palette?.select?.labelFocused || theme.palette.primary.main,
  },
  '&.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
    transform: 'translate(14px, -8px) scale(0.80)',
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
  },
}));

const StyledSelect = styled(({ customStyles: _customStyles, ...otherProps }) => (
  <MuiSelect {...otherProps} />
))(({ theme, customStyles }) => ({
  backgroundColor: customStyles?.bg || theme.palette?.select?.bg || theme.palette.background.paper,
  color: customStyles?.text || theme.palette?.select?.text || theme.palette.text.primary,
  border: `1px solid ${customStyles?.border || theme.palette?.select?.border || theme.palette.divider}`,
  '& .MuiSelect-icon': {
    color: customStyles?.icon || theme.palette?.select?.icon || theme.palette.primary.main,
  },
  borderRadius: 'var(--radius-6)',
  boxShadow: customStyles?.boxShadow || 'var(--shadow-soft)',
}));

const StyledMenuItem = styled(({ customStyles: _customStyles, ...otherProps }) => (
  <MenuItem {...otherProps} />
))(({ theme, customStyles }) => ({
  '&.Mui-selected': {
    backgroundColor:
      customStyles?.selectedRowBg || theme.palette?.select?.selectRowBg || theme.palette.action.selected,
    color: customStyles?.selectedRowText || theme.palette?.select?.selectRowText || theme.palette.text.primary,
  },
  '&:hover': {
    backgroundColor: customStyles?.hoverBg || theme.palette.action.hover,
  },
}));

const menuPropsFactory = (theme, customStyles) => ({
  PaperProps: {
    sx: {
      bgcolor: customStyles?.menuBg || theme.palette?.select?.openBg || theme.palette.background.default,
      color: customStyles?.menuText || theme.palette?.select?.openText || theme.palette.text.primary,
    },
  },
});

const MySelect = ({
  name,
  options = [],
  value,
  onChange,
  selectStyles = {},
  menuStyles = {},
  menuItemStyles = {},
  label = 'Select',
  labelStyles = {},
  inputProps = {},
  fullWidth = true,
  hideLabel = false,
  formControlSx = {},
  error = false,
  helperText = '',
  className = '',
  ...props
}) => {
  const theme = useTheme();

  const mergedLabelStyles = {
    color: theme.palette?.select?.label || theme.palette.text.secondary,
    ...labelStyles,
  };

  const labelId = `${name || 'select'}-label-id`;

  return (
    <FormControl
      fullWidth={fullWidth}
      variant="outlined"
      sx={formControlSx}
      error={error}
      className={className}
    >
      {!hideLabel && (
        <StyledInputLabel id={labelId} customStyles={mergedLabelStyles} shrink>
          {label}
        </StyledInputLabel>
      )}
      <StyledSelect
        labelId={labelId}
        name={name}
        value={value}
        size="small"
        onChange={onChange}
        customStyles={selectStyles}
        displayEmpty
        inputProps={inputProps}
        label={hideLabel ? undefined : label}
        MenuProps={{
          ...menuPropsFactory(theme, menuStyles),
        }}
        {...props}
      >
        {props.children}

        {options.map((option, index) => (
          <StyledMenuItem
            key={index}
            value={option.value ?? option.id}
            customStyles={menuItemStyles}
          >
            {option.label ?? option.name}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};

export { StyledMenuItem as MenuItem };
export default MySelect;
